(() => {
  if (customElements.get('sa-section-tab-nav')) return;

  class SaSectionTabNav extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-sa-tab-trigger]'));
      this.targetClasses = this.tabs
        .map((tab) => tab.dataset.targetClass?.trim())
        .filter(Boolean);
      this.bar = this.querySelector('.sa-section-tab-nav__bar');
      this.placeholder = this.querySelector('[data-sa-tab-nav-placeholder]');
      this.isFixed = false;
      this.naturalDocumentTop = 0;
      this.onScroll = this.onScroll.bind(this);
      this.onStickyOffsetUpdate = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      if (!this.tabs.length || !this.targetClasses.length || !this.bar || !this.placeholder) return;

      this.bindTabs();
      this.activateTab(this.tabs[0], false);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('sa:sticky-offset:updated', this.onStickyOffsetUpdate);

      requestAnimationFrame(() => {
        this.updateNaturalPosition();
        this.onScroll();
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('sa:sticky-offset:updated', this.onStickyOffsetUpdate);
    }

    bindTabs() {
      this.tabs.forEach((tab) => {
        if (tab.dataset.saTabBound === 'true') return;
        tab.dataset.saTabBound = 'true';

        tab.addEventListener('click', () => {
          this.activateTab(tab, true);
        });
      });
    }

    isDesktop() {
      return window.matchMedia('(min-width: 1000px)').matches;
    }

    getStickyTop() {
      const styles = getComputedStyle(document.documentElement);
      const announcement = parseFloat(styles.getPropertyValue('--announcement-bar-height')) || 0;
      const header = parseFloat(styles.getPropertyValue('--header-height')) || 0;
      // Mobile product sticky bar is fixed to the bottom — do not offset tab nav by its height.
      const stickyBarHeight = this.isDesktop()
        ? parseFloat(styles.getPropertyValue('--sa-product-sticky-bar-height')) || 0
        : 0;
      return announcement + header + stickyBarHeight;
    }

    getStickyOffset() {
      return this.getStickyTop() + (this.bar?.offsetHeight || 0) + 8;
    }

    updateNaturalPosition() {
      if (this.isFixed || !this.bar) return;
      this.naturalDocumentTop = this.bar.getBoundingClientRect().top + window.scrollY;
    }

    onResize() {
      if (this.isFixed) {
        this.placeholder.style.height = `${this.bar.offsetHeight}px`;
      } else {
        this.updateNaturalPosition();
      }
      this.onScroll();
    }

    shouldFix() {
      return window.scrollY + this.getStickyTop() >= this.naturalDocumentTop;
    }

    setFixed(shouldFix) {
      if (shouldFix === this.isFixed) return;

      if (shouldFix) {
        this.placeholder.style.height = `${this.bar.offsetHeight}px`;
        this.placeholder.removeAttribute('data-sa-tab-hidden');
        this.bar.classList.add('is-fixed');
        this.isFixed = true;
        return;
      }

      this.bar.classList.remove('is-fixed');
      this.placeholder.setAttribute('data-sa-tab-hidden', 'true');
      this.placeholder.style.height = '';
      this.isFixed = false;
      requestAnimationFrame(() => {
        this.updateNaturalPosition();
        this.onScroll();
      });
    }

    onScroll() {
      if (!this.bar || !this.placeholder) return;

      if (!this.isFixed) {
        this.updateNaturalPosition();
      }

      this.setFixed(this.shouldFix());
    }

    getTargetElements(className) {
      if (!className) return [];

      const escaped = className.replace(/[^\w-]/g, '');
      if (!escaped) return [];

      return Array.from(document.querySelectorAll(`.${escaped}`)).filter(
        (element) => !element.closest('sa-section-tab-nav'),
      );
    }

    activateTab(activeTab, shouldScroll) {
      const activeClass = activeTab.dataset.targetClass?.trim();
      if (!activeClass) return;

      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle('font-[500]', isActive);
        tab.classList.toggle('font-[400]', !isActive);
        tab.classList.toggle('text-[rgba(0,0,0,0.95)]', isActive);
        tab.classList.toggle('text-[rgba(0,0,0,0.5)]', !isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.targetClasses.forEach((className) => {
        const isActive = className === activeClass;
        this.getTargetElements(className).forEach((element) => {
          if (isActive) {
            element.removeAttribute('data-sa-tab-hidden');
          } else {
            element.setAttribute('data-sa-tab-hidden', 'true');
          }
          element.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
      });

      if (shouldScroll) {
        const target = this.getTargetElements(activeClass)[0];
        if (target) {
          const top = target.getBoundingClientRect().top + window.scrollY - this.getStickyOffset();
          window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
        }
      }
    }
  }

  customElements.define('sa-section-tab-nav', SaSectionTabNav);
})();
